import { blend, type GroundPlane } from './ground'

/**
 * The ground plane, seeded from WebXR hit-test instead of from depth.
 *
 * Hit-test hands back ARCore's own tracked plane — the same surface every other
 * implementation in the field uses for ground — and unlike `plane-detection` it
 * needs no Chrome flag. Four passes of fitting a plane to depth established
 * that the fit's RMS residual is a measurement of the *sensor*, not of the
 * algorithm, so no better fitting exists; this replaces the measurement with a
 * trackable.
 *
 * The hit-test source is requested once, lazily, because
 * `requestHitTestSource` is async and the frame loop is not.
 */

/** A hit whose surface normal is further than this from +Y is a wall, not floor. */
const MIN_UP = 0.85

export type HitPlaneState = 'idle' | 'waiting' | 'unsupported' | 'tracking' | 'stale'

export class HitPlane {
  private source: XRHitTestSource | null = null
  private requested = false
  private failed = false
  private lastHitAt = 0

  plane: GroundPlane | null = null

  /** How many hits have been accepted since the last reset. */
  hits = 0

  reset(): void {
    this.plane = null
    this.hits = 0
    this.lastHitAt = 0
  }

  dispose(): void {
    this.source?.cancel()
    this.source = null
    this.requested = false
    this.failed = false
    this.reset()
  }

  state(now: number, staleMs: number): HitPlaneState {
    if (this.failed) return 'unsupported'
    if (!this.plane) return this.requested ? 'waiting' : 'idle'
    return now - this.lastHitAt > staleMs ? 'stale' : 'tracking'
  }

  /**
   * Reads this frame's hit and folds it into the plane. Returns the plane,
   * which survives frames with no hit — a hit-test miss is the reticle pointing
   * at something untracked, not the floor ceasing to exist.
   */
  update(
    frame: XRFrame,
    refSpace: XRReferenceSpace,
    session: XRSession,
    now: number
  ): GroundPlane | null {
    this.ensureSource(session)
    if (!this.source) return this.plane

    const result = frame.getHitTestResults(this.source)[0]
    const pose = result?.getPose(refSpace)
    if (!pose) return this.plane

    const m = pose.transform.matrix
    // Column 1 of a hit pose is the surface normal; ARCore orients hit poses
    // with +Y out of the surface.
    const normalX = m[4]!
    const normalY = m[5]!
    const normalZ = m[6]!
    if (normalY < MIN_UP) return this.plane

    const fresh: GroundPlane = {
      height: m[13]!,
      slopeX: -normalX / normalY,
      slopeZ: -normalZ / normalY,
      originX: m[12]!,
      originZ: m[14]!,
      rms: 0,
      inliers: 0,
    }

    this.lastHitAt = now
    this.hits += 1
    // Same blend the depth fit uses: a plane re-derived every frame moves every
    // frame, and anything standing on it moves with it.
    this.plane = this.plane ? blend(this.plane, fresh) : fresh
    return this.plane
  }

  private ensureSource(session: XRSession): void {
    if (this.requested || this.failed) return
    this.requested = true

    session
      .requestReferenceSpace('viewer')
      .then((space) => session.requestHitTestSource?.({ space }))
      .then((source) => {
        if (source) this.source = source
        else this.failed = true
      })
      .catch(() => {
        this.failed = true
      })
  }
}
